// src/pages/all-purchases.jsx
import React, { useState, useCallback, useMemo, memo } from 'react';
import { AlertCircle, Music, Play, Pause, Download, Plus, User, Disc3, Search, ListMusic, ChevronRight, ArrowLeft, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAudioPlayerContext } from '../components/music/AudioPlayerProvider';
import { usePlaylist } from '../components/music/hooks/usePlaylist';
import { AddToPlaylistModal } from '../components/music/parts/AddToPlaylistModal';
import { usePurchaseModal } from '../components/music/hooks/usePurchaseModal';
import { PurchaseModal } from '../components/music/parts/PurchaseModal';
import { CREATE_PAYMENT_URL } from '../components/music/constants/paymentConfig';

// Updated TrackRow to handle the Purchase state
const TrackRow = memo(({ 
  track, 
  isActive, 
  isPlaying, 
  onPlayPause, 
  onDownload, 
  onAddToPlaylist, 
  onPurchase, 
  isSendingEmail,
  isPurchased,
  showDownloadButton, 
  showAddToPlaylist = true,
  onRemove
}) => {
  return (
    <motion.div
      layout="position"
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      className={`relative group overflow-hidden bg-[#121318]/80 backdrop-blur-xl border rounded-2xl p-3 sm:p-4 transition-all duration-300 ${
        isActive ? 'border-[#bd9f67]/60 bg-[#bd9f67]/10 shadow-lg shadow-[#bd9f67]/5' : 'border-white/10 hover:border-[#bd9f67]/30 hover:bg-[#181a22]'
      }`}
    >
      <div className="flex items-center justify-between gap-2 sm:gap-4 relative z-10">
        {/* LEFT: Play Button and Info */}
        <div className="flex items-center gap-3.5 flex-1 min-w-0">
          <motion.button
            whileTap={{ scale: 0.9 }}
            onClick={() => onPlayPause(track)}
            className={`flex-shrink-0 p-2.5 rounded-xl transition-all duration-200 ${
              isActive ? 'bg-[#bd9f67] text-gray-950 shadow-md shadow-[#bd9f67]/20' : 'bg-white/5 text-white/80 hover:bg-white/10 hover:text-white border border-white/10'
            }`}
          >
            {isActive && isPlaying ? (
              <Pause size={16} fill="currentColor" />
            ) : (
              <Play size={16} fill={isActive ? "currentColor" : "none"} className={!isActive ? "ml-0.5" : ""} />
            )}
          </motion.button>

          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 h-6">
              <h3 className={`font-bold text-sm sm:text-base truncate tracking-tight ${isActive ? 'text-[#bd9f67]' : 'text-white'}`}>
                {track.name}
              </h3>
              
              {/* Audio Visualizer */}
              <div className="w-4 flex-shrink-0 flex items-end gap-0.5 h-3 mb-1">
                {isActive && isPlaying && (
                  <>
                    <div className="w-0.5 bg-[#bd9f67] animate-[music-bar_0.8s_ease-in-out_infinite]" />
                    <div className="w-0.5 bg-[#bd9f67] animate-[music-bar_0.8s_ease-in-out_0.2s_infinite]" />
                    <div className="w-0.5 bg-[#bd9f67] animate-[music-bar_0.8s_ease-in-out_0.4s_infinite]" />
                  </>
                )}
              </div>
            </div>
            
            <div className="flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-white/50 mt-0.5">
              <div className="flex items-center gap-1.5 min-w-0">
                <User size={12} className={isActive ? 'text-[#bd9f67]' : 'text-white/40'} />
                <span className="truncate">{track.artist}</span>
              </div>
              {track.album && (
                <div className="flex items-center gap-1.5 min-w-0 border-l border-white/10 pl-3">
                  <span className="truncate opacity-80">{track.album}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* RIGHT: Action Buttons */}
        <div className="flex items-center gap-2 flex-shrink-0">
          {isPurchased ? (
            showDownloadButton && (
              <motion.button 
                whileTap={{ scale: 0.95 }} 
                onClick={() => onDownload(track)} 
                disabled={isSendingEmail} 
                className="flex items-center gap-1.5 px-3 py-2 bg-[#bd9f67]/15 hover:bg-[#bd9f67]/25 text-[#bd9f67] border border-[#bd9f67]/30 rounded-xl transition-all text-xs font-semibold"
              >
                <Download size={14} />
                <span className="hidden sm:inline">Download</span>
              </motion.button>
            )
          ) : (
            <motion.button 
              whileTap={{ scale: 0.95 }} 
              onClick={() => onPurchase(track)} 
              disabled={true}
              className="flex items-center gap-1.5 px-3 py-2 bg-amber-500/10 hover:bg-amber-500/20 text-amber-300 border border-amber-500/20 rounded-xl transition-all text-xs font-semibold"
            >
              <Music size={14} />
              <span className="hidden sm:inline">Buy Song</span>
            </motion.button>
          )}

          {/* ADD TO PLAYLIST */}
          {showAddToPlaylist && (
            <motion.button 
              whileTap={{ scale: 0.95 }} 
              onClick={() => onAddToPlaylist(track)} 
              className="flex items-center gap-1.5 px-3 py-2 bg-white/5 hover:bg-white/10 text-white/80 border border-white/10 rounded-xl transition-all text-xs font-semibold"
            >
              <Plus size={14} />
              <span className="hidden sm:inline">Playlist</span>
            </motion.button>
          )}

          {/* REMOVE FROM PLAYLIST */}
          {onRemove && (
            <motion.button 
              whileTap={{ scale: 0.95 }} 
              onClick={() => onRemove(track)} 
              className="flex items-center gap-1.5 px-3 py-2 bg-red-500/10 hover:bg-red-500/20 text-red-400 border border-red-500/20 rounded-xl transition-all text-xs font-semibold"
            >
              <Trash2 size={14} />
              <span className="hidden sm:inline">Remove</span>
            </motion.button>
          )}
        </div>
      </div>
    </motion.div>
  );
});

const ConfirmRemoveModal = ({ track, playlistName, isRemoving, onCancel, onConfirm }) => {
  if (!track) return null;

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 bg-black/80 backdrop-blur-md flex items-center justify-center z-50 p-4 font-sans antialiased"
      onClick={onCancel}
    >
      <motion.div
        initial={{ scale: 0.9, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        exit={{ scale: 0.9, opacity: 0 }}
        className="bg-[#121318] border border-white/10 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden text-white"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2.5 bg-red-500/10 border border-red-500/20 rounded-xl">
              <Trash2 size={20} className="text-red-400" />
            </div>
            <h3 className="text-lg font-bold text-white">Remove Track</h3>
          </div>
          <p className="text-xs sm:text-sm text-white/70 leading-relaxed">
            Remove <span className="text-[#bd9f67] font-semibold">{track.name}</span> from <span className="text-white font-semibold">{playlistName}</span>?
          </p>
        </div>
        <div className="flex items-center justify-end gap-2.5 p-4 bg-[#181a22] border-t border-white/5">
          <button
            onClick={onCancel}
            disabled={isRemoving}
            className="px-4 py-2.5 bg-white/5 hover:bg-white/10 text-white/70 border border-white/10 rounded-xl transition text-xs font-semibold"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isRemoving}
            className="px-4 py-2.5 bg-red-500/80 hover:bg-red-500 text-white rounded-xl transition text-xs font-bold shadow-lg shadow-red-500/20"
          >
            {isRemoving ? 'Removing...' : 'Remove'}
          </button>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default function AllPurchasesPage({ music, currentUser, purchases, showToast }) {
  const [activeTab, setActiveTab] = useState('songs'); 
  const [selectedPlaylist, setSelectedPlaylist] = useState(null);
  const [isSendingEmail, setIsSendingEmail] = useState(false);
  const [showAddToPlaylistModal, setShowAddToPlaylistModal] = useState(false);
  const [trackToAddToPlaylist, setTrackToAddToPlaylist] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [removingId, setRemovingId] = useState(null);
  const [trackToRemove, setTrackToRemove] = useState(null);

  const playlist = usePlaylist(currentUser);
  const player = useAudioPlayerContext();
  const { currentTrack, isPlaying } = player;
  
  const isPurchased = useCallback((musicId) => {
    if (purchases && typeof purchases.isPurchased === 'function') {
      return purchases.isPurchased(musicId);
    }
    return false;
  }, [purchases]);

  const purchase = usePurchaseModal(CREATE_PAYMENT_URL);

  const purchasedMusic = useMemo(() => {
    return music.filter(track => isPurchased(track.id));
  }, [music, isPurchased]);

  const displayMusicList = useMemo(() => {
    if (activeTab === 'playlists' && selectedPlaylist) {
        return selectedPlaylist.tracks || [];
    }
    return purchasedMusic;
  }, [activeTab, selectedPlaylist, purchasedMusic]);

  const filteredMusic = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return displayMusicList.filter(track => 
      track.name?.toLowerCase().includes(query) ||
      track.artist?.toLowerCase().includes(query) ||
      track.album?.toLowerCase().includes(query)
    );
  }, [displayMusicList, searchQuery]);

  const filteredPlaylists = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return (playlist.playlists || []).filter(pl => 
      pl.name?.toLowerCase().includes(query)
    );
  }, [playlist.playlists, searchQuery]);

  const handleDownload = useCallback(async (track) => {
    if (isPurchased(track.id)) {
      setIsSendingEmail(true);
      try {
        const sendEmailUrl = `${process.env.REACT_APP_SEND_MUSIC_EMAIL || 'https://us-central1-asidorsmusic.cloudfunctions.net/sendMusicViaEmail'}`;
        const response = await fetch(sendEmailUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ userId: currentUser.uid, musicId: track.id, userEmail: currentUser.email })
        });
        const data = await response.json();
        if (!response.ok || data.error) {
          showToast(`Failed: ${data.error || 'Unknown error'}`, 'error');
          setIsSendingEmail(false);
          return;
        }
        showToast(`✅ Sent to ${currentUser.email}!`, 'success');
        setIsSendingEmail(false);
      } catch (error) {
        showToast('Failed to send music.', 'error');
        setIsSendingEmail(false);
      }
    }
  }, [currentUser, showToast, isPurchased]);

  const handleOpenAddToPlaylist = useCallback((track) => {
    setTrackToAddToPlaylist(track);
    setShowAddToPlaylistModal(true);
  }, []);

  const handleOpenRemoveModal = useCallback((track) => {
    setTrackToRemove(track);
  }, []);

  const handleCancelRemove = useCallback(() => {
    setTrackToRemove(null);
  }, []);

  const handleConfirmRemove = useCallback(async () => {
    if (!trackToRemove || !selectedPlaylist) return;
    setRemovingId(trackToRemove.id);
    try {
      const success = await playlist.removeTrackFromPlaylist(selectedPlaylist.id, trackToRemove.id);
      if (success) {
        setSelectedPlaylist((prev) => prev
          ? { ...prev, tracks: (prev.tracks || []).filter((t) => t.id !== trackToRemove.id) }
          : prev
        );
        showToast('Removed from playlist', 'success');
      } else {
        showToast('Failed to remove from playlist.', 'error');
      }
    } catch (error) {
      showToast('Failed to remove from playlist.', 'error');
    } finally {
      setRemovingId(null);
      setTrackToRemove(null);
    }
  }, [trackToRemove, selectedPlaylist, playlist, showToast]);

  if (!currentUser) {
    return (
      <div className="w-full min-h-screen bg-[#08080a] text-white pt-24 flex items-center justify-center p-4 font-sans antialiased">
        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-[#121318] border border-white/10 rounded-2xl p-6 max-w-sm w-full shadow-2xl">
          <div className="flex items-start space-x-3">
            <AlertCircle className="text-[#bd9f67] flex-shrink-0 mt-0.5" size={20} />
            <div>
              <h2 className="text-base font-bold text-white">Sign In Required</h2>
              <p className="text-white/60 mt-1 text-xs leading-relaxed">Please sign in to access your personal music library and playlists.</p>
            </div>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <>
      <div className="w-full min-h-screen bg-[#08080a] text-white font-sans antialiased pt-24 pb-32 relative overflow-hidden">
        {/* Background Ambient Gold Glows */}
        <div className="absolute top-20 left-10 w-96 h-96 bg-[#bd9f67]/5 rounded-full blur-3xl pointer-events-none" />
        <div className="absolute top-1/3 right-10 w-80 h-80 bg-[#d0b07a]/5 rounded-full blur-3xl pointer-events-none" />

        <div className="max-w-7xl mx-auto relative z-10 px-4 sm:px-6">
          <div className="mb-8 flex flex-col gap-6">
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div className="flex items-center gap-3.5">
                <div className="p-3 bg-[#bd9f67]/10 border border-[#bd9f67]/20 rounded-2xl flex-shrink-0 text-[#bd9f67]">
                  <Disc3 size={22} />
                </div>
                <div>
                  <div className="inline-flex items-center gap-1.5 text-[#bd9f67] text-xs font-semibold tracking-wider uppercase mb-0.5">
                    Personal Collection
                  </div>
                  <h1 className="text-2xl sm:text-4xl font-bold tracking-tight text-white">My Library</h1>
                  <div className="flex items-center gap-6 mt-3 border-b border-white/5 pb-2">
                    <button 
                      onClick={() => { setActiveTab('songs'); setSelectedPlaylist(null); setSearchQuery(''); }}
                      className={`text-xs sm:text-sm font-bold uppercase tracking-wider transition-all relative pb-2 ${
                        activeTab === 'songs' ? 'text-[#bd9f67]' : 'text-white/40 hover:text-white'
                      }`}
                    >
                      Purchased ({purchasedMusic.length})
                      {activeTab === 'songs' && (
                        <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#bd9f67]" />
                      )}
                    </button>
                    <button 
                      onClick={() => { setActiveTab('playlists'); setSelectedPlaylist(null); setSearchQuery(''); }}
                      className={`text-xs sm:text-sm font-bold uppercase tracking-wider transition-all relative pb-2 ${
                        activeTab === 'playlists' ? 'text-[#bd9f67]' : 'text-white/40 hover:text-white'
                      }`}
                    >
                      Playlists ({playlist.playlists?.length || 0})
                      {activeTab === 'playlists' && (
                        <motion.div layoutId="tab-underline" className="absolute bottom-0 left-0 right-0 h-0.5 bg-[#bd9f67]" />
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Search Bar */}
              <div className="relative w-full md:w-72">
                <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-white/40" />
                <input 
                  type="text"
                  placeholder="Search tracks or playlists..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full bg-[#121318] border border-white/10 rounded-xl py-2.5 pl-10 pr-4 text-xs sm:text-sm text-white placeholder-white/30 focus:outline-none focus:border-[#bd9f67] transition-all"
                />
              </div>
            </div>
          </div>

          <AnimatePresence mode='wait'>
            {activeTab === 'songs' ? (
              <motion.div key="songs-tab" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
                {filteredMusic.length === 0 ? (
                  <div className="bg-[#121318]/50 border border-white/5 rounded-2xl p-12 text-center max-w-md mx-auto">
                    <p className="text-white/50 text-xs sm:text-sm">No purchased tracks found matching your query.</p>
                  </div>
                ) : (
                  filteredMusic.map((track) => (
                    <TrackRow 
                      key={track.id} 
                      track={track} 
                      isActive={currentTrack?.id === track.id}
                      isPlaying={isPlaying}
                      onPlayPause={(track) => player.handlePlayPause(track, filteredMusic)}
                      onDownload={handleDownload}
                      onAddToPlaylist={handleOpenAddToPlaylist}
                      isSendingEmail={isSendingEmail}
                      isPurchased={true}
                      showDownloadButton={true}
                    />
                  ))
                )}
              </motion.div>
            ) : (
              <motion.div key="playlists-tab" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
                {!selectedPlaylist ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {filteredPlaylists.length === 0 ? (
                      <div className="col-span-full bg-[#121318]/50 border border-white/5 rounded-2xl p-12 text-center max-w-md mx-auto">
                        <p className="text-white/50 text-xs sm:text-sm">No playlists available.</p>
                      </div>
                    ) : (
                      filteredPlaylists.map((pl) => (
                        <motion.div
                          key={pl.id}
                          onClick={() => setSelectedPlaylist(pl)}
                          whileHover={{ y: -2 }}
                          className="bg-[#121318]/80 backdrop-blur-xl border border-white/10 p-5 rounded-2xl flex items-center justify-between group cursor-pointer hover:border-[#bd9f67]/50 transition-all duration-300 shadow-xl"
                        >
                          <div className="flex items-center gap-4 min-w-0">
                            <div className="w-12 h-12 bg-[#bd9f67]/10 border border-[#bd9f67]/20 rounded-xl flex items-center justify-center text-[#bd9f67] flex-shrink-0">
                              <ListMusic size={22} />
                            </div>
                            <div className="min-w-0">
                              <h3 className="text-white font-bold text-sm truncate group-hover:text-[#bd9f67] transition-colors">{pl.name}</h3>
                              <p className="text-white/40 text-xs mt-0.5">{pl.tracks?.length || 0} tracks</p>
                            </div>
                          </div>
                          <ChevronRight size={18} className="text-white/30 group-hover:text-[#bd9f67] group-hover:translate-x-0.5 transition-all flex-shrink-0" />
                        </motion.div>
                      ))
                    )}
                  </div>
                ) : (
                  <div className="space-y-4">
                    <button 
                      onClick={() => setSelectedPlaylist(null)}
                      className="flex items-center gap-2 text-[#bd9f67] hover:underline transition-all text-xs font-semibold mb-4"
                    >
                      <ArrowLeft size={16} /> Back to Playlists
                    </button>
                    <div className="mb-6">
                      <h2 className="text-xl sm:text-2xl font-bold text-white tracking-tight">{selectedPlaylist.name}</h2>
                      <p className="text-white/50 text-xs mt-0.5">{filteredMusic.length} tracks</p>
                    </div>
                    <div className="space-y-3">
                      {filteredMusic.map((track) => (
                        <TrackRow 
                          key={track.id} 
                          track={track} 
                          isActive={currentTrack?.id === track.id}
                          isPlaying={isPlaying}
                          onPlayPause={(track) => player.handlePlayPause(track, filteredMusic)}
                          onDownload={handleDownload}
                          onAddToPlaylist={handleOpenAddToPlaylist}
                          isSendingEmail={isSendingEmail}
                          isPurchased={isPurchased(track.id)}
                          showDownloadButton={false}
                          showAddToPlaylist={false}
                          onPurchase={purchase.handlePurchaseClick}
                          onRemove={handleOpenRemoveModal}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </motion.div>
            )}
          </AnimatePresence>
        </div>
      </div>

      <ConfirmRemoveModal
        track={trackToRemove}
        playlistName={selectedPlaylist?.name}
        isRemoving={removingId === trackToRemove?.id}
        onCancel={handleCancelRemove}
        onConfirm={handleConfirmRemove}
      />

      <AddToPlaylistModal
        track={trackToAddToPlaylist}
        playlists={playlist.playlists}
        isOpen={showAddToPlaylistModal}
        onClose={() => { setShowAddToPlaylistModal(false); setTrackToAddToPlaylist(null); }}
        onAddToPlaylist={playlist.addTrackToPlaylist}
        currentUser={currentUser}
      />

      <PurchaseModal
        selectedTrack={purchase.selectedTrack}
        isOpen={purchase.showPriceModal}
        onClose={() => purchase.setShowPriceModal(false)}
        onSubmit={purchase.handleCheckout}
        isProcessing={purchase.processingPayment === purchase.selectedTrack?.id}
        purchaseSuccess={purchase.purchaseSuccess}
        price={purchase.PADDLE_PRICES.PRICE_15.amount}
        priceData={purchase.PADDLE_PRICES.PRICE_15}
        onPriceChange={purchase.setPrice}
        paymentMethod={purchase.paymentMethod}
        onPaymentMethodChange={purchase.setPaymentMethod}
        currentUser={currentUser}
      />

      <style>{`
        @keyframes music-bar { 0%, 100% { height: 4px; } 50% { height: 12px; } }
      `}</style>
    </>
  );
}